import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import HelpButton from './HelpButton';
import { CloudinaryImage, Gallery } from '../../types/Gallery';
import { getGalleryById, loadLiveGalleries } from '../../utils/GalleryAPI';
import { cloudinaryShareUrl } from '../../utils/imageSeo';
import sitePaths from '../../utils/sitePaths';
import './SearchPhotosManagement.css';

interface DraftPhoto {
  publicId: string;
  url: string;
  width: number;
  height: number;
  alt: string;
  caption: string;
  galleryId: string;
}

interface StoredRecord {
  home?: DraftPhoto[];
  shareImage?: { url?: string; alt?: string; publicId?: string } | null;
  categories?: Record<string, DraftPhoto[]>;
}

const HOME_MIN = 4;
const HOME_MAX = 8;
const CATEGORY_MAX = 4;
const PLACE_PATTERN = /\b(cape town|noordhoek|hout bay|camps bay|clifton|sea point|muizenberg|kalk bay|simons? town|kommetjie|llandudno|constantia|tokai|fish hoek|chapman'?s peak|cape point|stellenbosch|franschhoek|winelands|city bowl|woodstock|observatory|green point|table mountain|lion'?s head|signal hill|waterfront|kirstenbosch|newlands|claremont|wynberg|rondebosch|blouberg|somerset west|hermanus|western cape|south africa)\b/i;

function photoUrl(image: CloudinaryImage): string {
  const url = image.secure_url || image.url || '';
  return /^https:\/\/res\.cloudinary\.com\//i.test(url) ? url : '';
}

function savedAlt(image: CloudinaryImage): string {
  return (image.alt || image.context?.alt || '').trim();
}

function savedCaption(image: CloudinaryImage): string {
  return (image.context?.caption || '').trim();
}

function wordCount(value: string): number {
  return value.trim().split(/\s+/).filter(Boolean).length;
}

function altWarnings(alt: string): string[] {
  const text = alt.trim();
  if (!text) return ['Alt text is missing.'];
  const warnings: string[] = [];
  const words = wordCount(text);
  if (words < 5) warnings.push('Under 5 words.');
  if (words > 15) warnings.push('Over 15 words.');
  if (!PLACE_PATTERN.test(text)) warnings.push('Missing a place, such as Cape Town, Noordhoek, or Hout Bay.');
  return warnings;
}

function draftFromImage(image: CloudinaryImage, gallery: Gallery | null): DraftPhoto {
  const alt = savedAlt(image);
  const caption = savedCaption(image);
  const galleryName = (gallery?.name || '').trim();
  return {
    publicId: image.publicId || image.public_id || '',
    url: photoUrl(image),
    width: image.width || 0,
    height: image.height || 0,
    alt: alt || (caption && caption.toLowerCase() !== galleryName.toLowerCase() ? caption : ''),
    caption: alt && caption && caption !== alt ? caption : '',
    galleryId: gallery ? String(gallery.id) : '',
  };
}

function cleanDraft(photo: DraftPhoto): DraftPhoto {
  return {
    publicId: photo.publicId || '',
    url: photo.url,
    width: photo.width || 0,
    height: photo.height || 0,
    alt: photo.alt || '',
    caption: photo.caption || '',
    galleryId: photo.galleryId || '',
  };
}

const SearchPhotosManagement: React.FC = () => {
  const filters = sitePaths.GALLERY_FILTERS || [];
  const [home, setHome] = useState<DraftPhoto[]>([]);
  const [shareUrl, setShareUrl] = useState('');
  const [categories, setCategories] = useState<Record<string, DraftPhoto[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pickerTarget, setPickerTarget] = useState<string | null>(null);
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [openGallery, setOpenGallery] = useState<Gallery | null>(null);
  const [galleryPhotos, setGalleryPhotos] = useState<CloudinaryImage[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerError, setPickerError] = useState('');
  const [search, setSearch] = useState('');

  const applyRecord = (record: StoredRecord) => {
    const nextHome = Array.isArray(record.home) ? record.home.map(cleanDraft) : [];
    setHome(nextHome);
    const savedShare = record.shareImage?.url || '';
    setShareUrl(nextHome.some((photo) => photo.url === savedShare) ? savedShare : (nextHome[0]?.url || ''));
    const nextCategories: Record<string, DraftPhoto[]> = {};
    filters.forEach((filter: { slug: string }) => {
      const list = record.categories && Array.isArray(record.categories[filter.slug])
        ? record.categories[filter.slug].map(cleanDraft)
        : [];
      nextCategories[filter.slug] = list;
    });
    setCategories(nextCategories);
  };

  const loadRecord = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/search-photos');
      if (!response.ok) throw new Error('The saved photographs could not be loaded.');
      applyRecord(await response.json());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'The saved photographs could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecord();
    // The filter list is fixed for the life of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentList = pickerTarget === 'home'
    ? home
    : (pickerTarget ? categories[pickerTarget] || [] : []);
  const currentMax = pickerTarget === 'home' ? HOME_MAX : CATEGORY_MAX;

  const openPicker = (target: string) => {
    setPickerTarget(target);
    setOpenGallery(null);
    setGalleryPhotos([]);
    setSearch('');
    setPickerError('');
    setPickerLoading(true);
    loadLiveGalleries('cards').then((loaded) => {
      setGalleries(loaded.filter((item) => item.published !== false));
      setPickerLoading(false);
    }).catch(() => {
      setPickerError('The galleries could not be loaded.');
      setPickerLoading(false);
    });
  };

  const chooseGallery = (gallery: Gallery) => {
    setOpenGallery(gallery);
    setSearch('');
    setPickerError('');
    setPickerLoading(true);
    getGalleryById(String(gallery.id)).then((full) => {
      const images = (full?.images || []).filter((image) => photoUrl(image));
      setGalleryPhotos(images);
      if (!images.length) setPickerError('This gallery has no photographs to choose.');
      setPickerLoading(false);
    }).catch(() => {
      setGalleryPhotos([]);
      setPickerError('This gallery could not be opened.');
      setPickerLoading(false);
    });
  };

  const addPhoto = (image: CloudinaryImage) => {
    if (!pickerTarget) return;
    const draft = draftFromImage(image, openGallery);
    if (!draft.url) return;
    const update = (list: DraftPhoto[]) => {
      if (list.some((item) => item.url === draft.url) || list.length >= currentMax) return list;
      return [...list, draft];
    };
    if (pickerTarget === 'home') {
      setHome((list) => {
        const next = update(list);
        if (!shareUrl && next[0]) setShareUrl(next[0].url);
        return next;
      });
      return;
    }
    setCategories((current) => ({ ...current, [pickerTarget]: update(current[pickerTarget] || []) }));
  };

  const updatePhoto = (target: string, url: string, field: 'alt' | 'caption', value: string) => {
    const change = (list: DraftPhoto[]) => list.map((photo) => (
      photo.url === url ? { ...photo, [field]: value } : photo
    ));
    if (target === 'home') setHome((list) => change(list));
    else setCategories((current) => ({ ...current, [target]: change(current[target] || []) }));
  };

  const movePhoto = (url: string, direction: -1 | 1) => {
    setHome((list) => {
      const index = list.findIndex((photo) => photo.url === url);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= list.length) return list;
      const next = list.slice();
      const [item] = next.splice(index, 1);
      next.splice(nextIndex, 0, item);
      return next;
    });
  };

  const removePhoto = (target: string, url: string) => {
    if (target === 'home') {
      setHome((list) => {
        const next = list.filter((photo) => photo.url !== url);
        if (shareUrl === url) setShareUrl(next[0]?.url || '');
        return next;
      });
      return;
    }
    setCategories((current) => ({
      ...current,
      [target]: (current[target] || []).filter((photo) => photo.url !== url),
    }));
  };

  const missingAlt = useMemo(() => {
    const homeMissing = home.some((photo) => !photo.alt.trim());
    const categoryMissing = Object.values(categories).some((list) => list.some((photo) => !photo.alt.trim()));
    return homeMissing || categoryMissing;
  }, [home, categories]);

  const save = async () => {
    setError('');
    setSuccess('');
    if (home.length < HOME_MIN || home.length > HOME_MAX) {
      setError(`Choose between ${HOME_MIN} and ${HOME_MAX} homepage photographs.`);
      return;
    }
    if (missingAlt) {
      setError('Every photograph needs alt text before it can be saved.');
      return;
    }
    if (!home.some((photo) => photo.url === shareUrl)) {
      setError('Choose which photograph is used when the link is shared.');
      return;
    }
    const share = home.find((photo) => photo.url === shareUrl);
    setSaving(true);
    try {
      const response = await fetch('/api/search-photos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          home,
          shareImage: { publicId: share?.publicId || '', url: shareUrl, alt: share?.alt || '' },
          categories,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'The photographs could not be saved.');
      applyRecord(body.searchPhotos || body);
      setSuccess('Saved. These photographs are now in the page Google downloads.');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'The photographs could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const sharePhoto = home.find((photo) => photo.url === shareUrl) || home[0];
  const sharePreview = sharePhoto ? cloudinaryShareUrl(sharePhoto.url) : '';
  const query = search.trim().toLowerCase();
  const visibleGalleries = galleries.filter((item) => !query || item.name.toLowerCase().includes(query));
  const visiblePhotos = galleryPhotos.filter((image) => {
    if (!photoUrl(image)) return false;
    if (!query) return true;
    return [savedAlt(image), savedCaption(image), image.filename || ''].join(' ').toLowerCase().includes(query);
  });

  const renderPhoto = (photo: DraftPhoto, target: string, index: number, total: number) => {
    const warnings = altWarnings(photo.alt);
    const missing = !photo.alt.trim();
    return (
      <li key={photo.url} className="search-photo-row">
        <img src={photo.url} alt="" />
        <div className="search-photo-fields">
          {target === 'home' ? (
            <label className="search-photo-share">
              <input
                type="radio"
                name="search-share-photo"
                checked={shareUrl === photo.url}
                onChange={() => setShareUrl(photo.url)}
              />
              Share image
            </label>
          ) : null}
          <label>
            Alt text
            <input
              value={photo.alt}
              maxLength={160}
              aria-invalid={missing}
              onChange={(event) => updatePhoto(target, photo.url, 'alt', event.target.value)}
            />
          </label>
          <p className="search-photo-hint">Describe the subject and the Cape Town location, 5–15 words.</p>
          {warnings.length ? (
            <ul className={`search-photo-warnings${missing ? ' is-blocking' : ''}`}>
              {warnings.map((warning) => <li key={warning}>{warning}</li>)}
            </ul>
          ) : null}
          <label>
            Caption, optional
            <input
              value={photo.caption}
              onChange={(event) => updatePhoto(target, photo.url, 'caption', event.target.value)}
            />
          </label>
        </div>
        <div className="search-photo-actions">
          {target === 'home' ? (
            <>
              <button type="button" onClick={() => movePhoto(photo.url, -1)} disabled={index === 0}>Up</button>
              <button type="button" onClick={() => movePhoto(photo.url, 1)} disabled={index === total - 1}>Down</button>
            </>
          ) : null}
          <button type="button" onClick={() => removePhoto(target, photo.url)}>Remove</button>
        </div>
      </li>
    );
  };

  return (
    <div className="search-photos">
      <div className="search-photos-intro">
        <p>
          Choose the photographs Google receives with the homepage, and the full-frame picture used when a link is shared. They are not shown as a row on the page.
          Until you save a selection, Google receives the first photograph from each featured gallery.
        </p>
        <HelpButton section="search-photos" tooltip="Photos for Google help" />
      </div>
      {error ? <p className="search-photos-error" role="alert">{error}</p> : null}
      {success ? <p className="search-photos-success" role="status">{success}</p> : null}
      {loading ? <p>Loading saved photographs…</p> : null}

      <section className="search-photos-panel" aria-label="Homepage photographs">
        <header>
          <h2>Homepage</h2>
          <p>{home.length} of {HOME_MIN}–{HOME_MAX} photographs</p>
        </header>
        <ul className="search-photo-list">
          {home.map((photo, index) => renderPhoto(photo, 'home', index, home.length))}
        </ul>
        <button type="button" onClick={() => openPicker('home')} disabled={home.length >= HOME_MAX}>
          Add a homepage photograph
        </button>
      </section>

      <details className="search-photos-panel">
        <summary>Gallery category pages</summary>
        <p className="search-photo-hint">Optional. Up to four photographs for Google on each category page. They are not shown on the page, because those galleries are already listed there. Leave a category empty to use the first photograph from each gallery in that category.</p>
        {filters.map((filter: { slug: string; name: string }) => {
          const list = categories[filter.slug] || [];
          return (
            <section key={filter.slug} className="search-category">
              <header>
                <h3>{filter.name}</h3>
                <p>{list.length} of {CATEGORY_MAX}</p>
              </header>
              <ul className="search-photo-list">
                {list.map((photo, index) => renderPhoto(photo, filter.slug, index, list.length))}
              </ul>
              <button type="button" onClick={() => openPicker(filter.slug)} disabled={list.length >= CATEGORY_MAX}>
                Add a {filter.name} photograph
              </button>
            </section>
          );
        })}
      </details>

      <section className="search-photos-preview" aria-label="Previews">
        <h2>What Google and a shared link can show</h2>
        <div className="search-google">
          <p className="search-google-url">carmenlorraine.com</p>
          <p className="search-google-title">Carmen Lorraine Photography | Cape Town Photographer</p>
          <p>Creative, natural photography in Cape Town by Carmen Lorraine.</p>
          {home.length ? (
            <ul>
              {home.map((photo) => (
                <li key={photo.url}>
                  <img src={photo.url} alt="" />
                  <span>{photo.alt || 'Alt text missing'}</span>
                </li>
              ))}
            </ul>
          ) : <p>Add photographs to preview the result.</p>}
        </div>
        <div className="search-social">
          <p>Share image, 1200×630</p>
          {sharePreview ? (
            <img src={sharePreview} alt={sharePhoto?.alt || ''} width={1200} height={630} />
          ) : <p>The share image is one of the homepage photographs.</p>}
        </div>
      </section>

      <div className="search-photos-save">
        <button type="button" onClick={save} disabled={saving || loading || missingAlt || home.length < HOME_MIN}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={loadRecord} disabled={saving || loading}>Reload</button>
      </div>

      {pickerTarget ? createPortal(
        <div className="search-picker-backdrop" role="presentation">
          <div className="search-picker" role="dialog" aria-modal="true" aria-label="Choose a photograph">
            <header className="search-picker-head">
              <h2>{openGallery ? openGallery.name : 'Choose a gallery'}</h2>
              <button type="button" onClick={() => setPickerTarget(null)}>Close</button>
            </header>
            <p>The original photograph is used. Nothing is uploaded again. {currentList.length} of {currentMax} selected.</p>
            <input
              value={search}
              placeholder={openGallery ? 'Search photographs' : 'Search galleries'}
              onChange={(event) => setSearch(event.target.value)}
            />
            {pickerError ? <p className="search-photos-error">{pickerError}</p> : null}
            {pickerLoading ? <p>Loading…</p> : null}
            {!openGallery && !pickerLoading ? (
              <ul className="search-picker-galleries">
                {visibleGalleries.map((gallery) => {
                  const coverImage = gallery.heroImage || gallery.images?.[0];
                  const cover = coverImage?.secure_url || coverImage?.url || '';
                  return (
                    <li key={gallery.id}>
                      <button type="button" onClick={() => chooseGallery(gallery)}>
                        {/^https:\/\//i.test(cover) ? <img src={cover} alt="" /> : <span className="search-picker-empty" aria-hidden="true" />}
                        <strong>{gallery.name}</strong>
                        <em>{gallery.imageCount || gallery.images?.length || 0} photographs</em>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {openGallery && !pickerLoading ? (
              <>
                <button type="button" onClick={() => { setOpenGallery(null); setSearch(''); }}>Back to galleries</button>
                <ul className="search-picker-photos">
                  {visiblePhotos.map((image) => {
                    const src = photoUrl(image);
                    const already = currentList.some((photo) => photo.url === src);
                    return (
                      <li key={src}>
                        <button type="button" onClick={() => addPhoto(image)} disabled={already || currentList.length >= currentMax}>
                          <img src={src} alt={savedAlt(image)} />
                          <span>{already ? 'Added' : 'Add'}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : null}
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  );
};

export default SearchPhotosManagement;

